import React, { useState, useEffect, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  signInWithGoogle, signInWithMicrosoft, signUpWithEmail, resendVerification, signOut, startTrial, auth as fbAuth,
} from "../../auth/firebaseAuth";
// Reuse the sign-in page's full-width, two-panel gradient design (its own font
// base, so it doesn't inherit the board's large .App font); StartTrial.css layers
// StartTrial-specific sizing on top (scoped to .start-trial).
import "../Signin/Signin.css";
import "./StartTrial.css";

// The care-circle name is captured before sign-in and must survive an OAuth
// popup, an email-verification reload, AND the round-trip to Stripe Checkout, so
// it lives in localStorage until the Care Circle is actually created.
const PENDING_KEY = "pendingFacilityName";
const PENDING_TERMS = "pendingTermsAccepted";

function friendly(err) {
  const code = err && err.code;
  if (code === "auth/email-already-in-use") return "An account with that email already exists — sign in instead.";
  if (code === "auth/weak-password") return "Password should be at least 6 characters.";
  if (code === "auth/invalid-email") return "That doesn't look like a valid email.";
  if (code === "auth/popup-closed-by-user") return "Sign-up was cancelled.";
  if (code === "auth/account-exists-with-different-credential")
    return "You already have an account with this email — sign in with the method you used originally.";
  return (err && err.message) || "Something went wrong. Please try again.";
}

export default function StartTrial() {
  const auth = useSelector((state) => state.auth);
  const checkout = new URLSearchParams(window.location.search).get("checkout");
  const [facility, setFacility] = useState(() => localStorage.getItem(PENDING_KEY) || "");
  const [accepted, setAccepted] = useState(() => localStorage.getItem(PENDING_TERMS) === "1");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const beganRef = useRef(false); // start the trial once after sign-in, even though the popup left busy=true

  const setTerms = (v) => { setAccepted(v); localStorage.setItem(PENDING_TERMS, v ? "1" : "0"); };

  const termsBox = (
    <label className="st-terms">
      <input type="checkbox" checked={accepted} onChange={(e) => setTerms(e.target.checked)} />
      <span>By checking this box, I agree to the{" "}
        <a href="/terms" target="_blank" rel="noreferrer" className="si-link">Terms of Service</a>{" "}
        and confirm I'm authorized to accept them on behalf of my Care Circle.</span>
    </label>
  );

  // Kick off the trial: comp -> Care Circle created (go to board); otherwise ->
  // redirect to Stripe Checkout.
  const begin = async (name) => {
    setBusy(true); setError(null);
    try {
      const res = await startTrial(name, localStorage.getItem(PENDING_TERMS) === "1");
      if (res && res.url) { window.location.assign(res.url); return; } // to Stripe
      localStorage.removeItem(PENDING_KEY); localStorage.removeItem(PENDING_TERMS);
      window.location.assign("/home"); // comped -> provisioned
    } catch (err) {
      beganRef.current = false; // allow another attempt
      setBusy(false);
      setError(friendly(err));
    }
  };

  // After sign-in with a verified email and a pending care-circle name, begin the
  // trial automatically (skip on the post-checkout return — provisioning is via
  // the webhook there).
  useEffect(() => {
    if (checkout) return;
    if (auth && !auth.tenantId && auth.emailVerified && !beganRef.current) {
      const pending = (localStorage.getItem(PENDING_KEY) || "").trim();
      if (pending) { beganRef.current = true; begin(pending); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, checkout]);

  // While waiting on email verification (the user clicks the link in a different
  // tab), poll here so this page advances on its own instead of needing a manual
  // reload. Once verified, reload so onAuthChange re-reads a fresh token and the
  // trial kicks off automatically.
  useEffect(() => {
    if (checkout) return;
    if (!(auth && !auth.tenantId && auth.emailVerified === false)) return;
    const id = setInterval(async () => {
      try {
        await fbAuth.currentUser?.reload();
        if (fbAuth.currentUser?.emailVerified) {
          clearInterval(id);
          window.location.reload();
        }
      } catch (_) { /* keep polling */ }
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, checkout]);

  // Post-checkout success: the webhook provisions the Care Circle asynchronously.
  // Poll the token until the tenant claim appears, then go to the board.
  useEffect(() => {
    if (checkout !== "success") return;
    let stop = false;
    const tick = async () => {
      const user = fbAuth.currentUser;
      if (user) {
        const { claims } = await user.getIdTokenResult(true);
        if (claims.tenantId) { localStorage.removeItem(PENDING_KEY); localStorage.removeItem(PENDING_TERMS); window.location.assign("/home"); return; }
      }
      if (!stop) setTimeout(tick, 2500);
    };
    tick();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout]);

  const startWith = (fn) => {
    const facilityName = facility.trim();
    if (!facilityName) { setError("Enter a name for your Care Circle first."); return; }
    if (!accepted) { setError("Please accept the Terms of Service to continue."); return; }
    localStorage.setItem(PENDING_KEY, facilityName);
    setBusy(true); setError(null);
    fn().catch((err) => { setBusy(false); setError(friendly(err)); });
  };

  const createAccount = (e) => {
    e.preventDefault();
    const facilityName = facility.trim();
    if (!facilityName) { setError("Enter a name for your Care Circle first."); return; }
    if (!accepted) { setError("Please accept the Terms of Service to continue."); return; }
    if (!name.trim()) { setError("Enter your name."); return; }
    if (!email || !password) { setError("Enter your email and a password."); return; }
    localStorage.setItem(PENDING_KEY, facilityName);
    setBusy(true); setError(null);
    signUpWithEmail(email, password, name).catch((err) => { setBusy(false); setError(friendly(err)); });
  };

  if (auth && auth.tenantId) return <Navigate to="/home" replace />;

  let title = "Create your Care Circle";
  let sub = "Start your 30-day free trial. You won't be charged until the trial ends, and you can cancel anytime.";
  let body;
  if (checkout === "success") {
    title = "Almost there";
    sub = "Finalizing your account…";
    body = <p className="si-muted">This takes a few seconds.</p>;
  } else if (checkout === "cancel") {
    title = "Checkout cancelled";
    sub = "Your card was not charged.";
    body = (
      <>
        <button className="si-btn si-btn-primary" disabled={busy} onClick={() => begin((facility || localStorage.getItem(PENDING_KEY) || "").trim())}>
          Try again
        </button>
        <p className="si-muted" style={{ fontSize: "0.86rem", marginTop: "0.7rem" }}>
          <Link to="/" className="si-link">Back to home</Link>
        </p>
      </>
    );
  } else if (auth === null) {
    body = <p className="si-muted">Loading…</p>;
  } else if (busy && auth && !auth.tenantId && auth.emailVerified) {
    body = <p className="si-muted">Starting your trial…</p>;
  } else if (auth && !auth.tenantId && auth.emailVerified === false) {
    title = "Verify your email";
    sub = <>Signed in as <strong>{auth.email}</strong>.</>;
    body = (
      <>
        <p className="si-info">
          Verify your email to continue setting up <strong>{facility || "your Care Circle"}</strong>.
          Check your inbox, click the link, then reload this page.
        </p>
        <button className="si-btn si-btn-oauth" disabled={busy}
          onClick={() => { setBusy(true); setError(null); resendVerification().then(() => { setBusy(false); setNotice("Verification email sent."); }).catch((err) => { setBusy(false); setError(friendly(err)); }); }}>
          Resend verification email
        </button>
        <button className="si-btn si-btn-oauth" onClick={() => signOut()}>Sign out</button>
      </>
    );
  } else if (auth && !auth.tenantId) {
    sub = <>Signed in as <strong>{auth.email}</strong>. Name your Care Circle to continue.</>;
    body = (
      <>
        <input className="si-field" placeholder="Care Circle name (e.g. The Smith Family)" value={facility} onChange={(e) => setFacility(e.target.value)} />
        {termsBox}
        <button className="si-btn si-btn-primary" disabled={busy}
          onClick={() => {
            if (!facility.trim()) { setError("Enter a name for your Care Circle first."); return; }
            if (!accepted) { setError("Please accept the Terms of Service to continue."); return; }
            begin(facility.trim());
          }}>
          {busy ? "Please wait…" : "Continue to secure checkout"}
        </button>
      </>
    );
  } else {
    body = (
      <>
        <input className="si-field" placeholder="Care Circle name (e.g. The Smith Family)" value={facility} onChange={(e) => setFacility(e.target.value)} />
        {termsBox}
        <button className="si-btn si-btn-oauth" disabled={busy} onClick={() => startWith(signInWithGoogle)}>Continue with Google</button>
        <button className="si-btn si-btn-oauth" disabled={busy} onClick={() => startWith(signInWithMicrosoft)}>Continue with Microsoft</button>
        <div className="si-divider"><hr /> or <hr /></div>
        <form onSubmit={createAccount}>
          <input className="si-field" type="text" placeholder="Your name (e.g. Mark R.)" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="si-field" type="email" placeholder="Email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="si-field" type="password" placeholder="Choose a password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" className="si-btn si-btn-primary" disabled={busy}>{busy ? "Please wait…" : "Create account & continue"}</button>
        </form>
        <p className="si-muted" style={{ fontSize: "0.86rem", marginTop: "0.8rem" }}>
          Already have an account? <Link to="/signin" className="si-link">Sign in</Link>
        </p>
      </>
    );
  }

  return (
    <div className="signin-page start-trial">
      <aside className="signin-brand">
        <div className="signin-brand-inner">
          <div className="signin-brand-logo">Mary's Bulletin Board</div>
          <div className="signin-brand-tag">Stay close to your <span className="grad">loved one</span>, from anywhere.</div>
          <ul className="signin-points">
            <li>Post notes, photos &amp; reminders to the screen in their room</li>
            <li>Invite your whole family to one private Care Circle</li>
            <li>We ship a ready-to-use screen — just plug it in</li>
            <li>30-day free trial — cancel anytime</li>
          </ul>
        </div>
      </aside>
      <main className="signin-main">
        <div className="signin-card">
          <div className="signin-card-logo">Mary's Bulletin Board</div>
          <h1 className="signin-title">{title}</h1>
          <p className="signin-sub">{sub}</p>
          {body}
          {error && <p className="si-msg si-error">{error}</p>}
          {notice && <p className="si-msg si-notice">{notice}</p>}
        </div>
      </main>
    </div>
  );
}
